import { App, MerchantPlan, MerchantPlanType, MerchantPlanModule } from '../models/app';
import { collectionIsASubset } from '../utils/helpers';
import { Ref } from '../models/common';
import { EntityState } from '@reduxjs/toolkit';

const isClassicPlan = (planType: MerchantPlanType): boolean => planType === MerchantPlanType.CLASSIC;
const isRegisterPlan = (planType: MerchantPlanType): boolean => planType === MerchantPlanType.REGISTER;
const isRegisterLitePlan = (planType: MerchantPlanType): boolean => planType === MerchantPlanType.REGISTER_LITE;
const isPaymentPlan = (planType: MerchantPlanType): boolean => (planType === MerchantPlanType.PAYMENTS_PLUS || planType === MerchantPlanType.PAYMENTS);

interface UsableMerchantPlan extends Ref {
  sortOrder: number;
  title: string;
  planTranslationKey: PlanType;
}

enum PlanType {
  IncludesApp = 'merchant.plans.included',
  SupportApp = 'merchant.plans.install_separately'
}

const planSortOrder = (plan: MerchantPlan): number => {
  const { type } = plan;
  if (isPaymentPlan(type)) {
    return 1;
  } else if (isRegisterLitePlan(type)) {
    return 2;
  } else if (isRegisterPlan(type)) {
    return 3;
  } else if (isClassicPlan(type)) {
    return 4;
  }
  return 5;
};

const planName = (plan: MerchantPlan): string => {
  const { type } = plan;
  const tKey: Record<MerchantPlanType, string> = {
    [MerchantPlanType.PAYMENTS_PLUS]: 'merchant.plans.payments_plus_plan',
    [MerchantPlanType.PAYMENTS]: 'merchant.plans.payments_plan',
    [MerchantPlanType.REGISTER_LITE]: 'merchant.plans.register_lite_plan',
    [MerchantPlanType.CLASSIC]: 'merchant.plans.classic_service_plan',
    [MerchantPlanType.REGISTER]: 'merchant.plans.register_plan',
    [MerchantPlanType.QSR]: 'merchant.plans.qsr_plan',
    [MerchantPlanType.DINING]: 'merchant.plans.table_service_plan',
    [MerchantPlanType.NO_HARDWARE]: 'merchant.plans.no_hardware_plan',
  };
  return tKey[type];
};

export const planIncludesApp = (plan: MerchantPlan, app: App, checkingForCurrentPlan?: boolean): boolean => {
  if (!checkingForCurrentPlan && isClassicPlan(plan.type)) return false;
  const includedApps = plan.appBundle.includedApps?.elements || [];
  return !includedApps.every(includedApp => app.id !== includedApp.id);
};

export const planSupportsApp = (plan: MerchantPlan, app: App, checkingForCurrentPlan?: boolean): boolean => {
  if (!checkingForCurrentPlan && isClassicPlan(plan.type)) return false;

  const planModules = (plan.modules?.elements || []) as MerchantPlanModule[];
  const appModules = (app.modules?.elements || []) as MerchantPlanModule[];

  return collectionIsASubset<MerchantPlanModule>(appModules, planModules, 'id');
};

export const appInstallRequiresAnotherPlan = (app: App, currentPlan: MerchantPlan): boolean => {
  return !!currentPlan && !app.installed && !planIncludesApp(currentPlan, app, true) && !planSupportsApp(currentPlan, app, true);
};

const getUsablePlan = (plan: MerchantPlan, planType: PlanType, appName: string): UsableMerchantPlan => {
  return {
    id: plan.id,
    planTranslationKey: planType,
    sortOrder: planSortOrder(plan),
    title: planName(plan),
  };
};

export const getUsableMerchantPlans = (app: App, plans: EntityState<MerchantPlan>, unsupportedPlanId: Uuid = ''): UsableMerchantPlan[] => {
  let usableMerchantPlans: UsableMerchantPlan[] = [];

  plans.ids.forEach((planId) => {
    const plan = plans.entities[planId];
    if (!plan || planId === unsupportedPlanId) return;

    if (planIncludesApp(plan, app)) {
      usableMerchantPlans.push(getUsablePlan(plan, PlanType.IncludesApp, app.name));
    } else if (planSupportsApp(plan, app)) {
      usableMerchantPlans.push(getUsablePlan(plan, PlanType.SupportApp, app.name));
    }
  });

  return usableMerchantPlans.sort((a, b) => a.sortOrder - b.sortOrder);
};
